import type { EChartsOption } from 'echarts';

import type { SeriesPoint } from './model';

const compact = new Intl.NumberFormat('en-AU', { notation: 'compact', maximumFractionDigits: 1 });
const whole = new Intl.NumberFormat('en-AU', { maximumFractionDigits: 0 });

const axisLabel = { formatter: (value: number) => compact.format(value) };

export function trendOption(
  points: ReadonlyArray<SeriesPoint>,
  kind: 'line' | 'bar' = 'line',
  ratio = false,
): EChartsOption {
  const dates = [...new Set(points.map((point) => point.date))].sort();
  const names = [...new Set(points.map((point) => point.series))];
  const lookup = new Map(points.map((point) => [`${point.date}|${point.series}`, point.value]));
  return {
    tooltip: { trigger: 'axis' },
    legend: { type: 'scroll', top: 0 },
    grid: { left: 56, right: 16, top: 36, bottom: 56 },
    xAxis: { type: 'category', data: dates },
    yAxis: { type: 'value', axisLabel: ratio ? { formatter: (value: number) => value.toFixed(4) } : axisLabel },
    dataZoom: dates.length > 60 ? [{ type: 'slider', bottom: 8 }] : undefined,
    series: names.map((name) => ({
      name,
      type: kind,
      stack: kind === 'bar' && !ratio ? 'total' : undefined,
      showSymbol: false,
      data: dates.map((date) => lookup.get(`${date}|${name}`) ?? (ratio ? null : 0)),
    })),
  };
}

export function barOption(
  categories: ReadonlyArray<string>,
  series: ReadonlyArray<{ name: string; data: ReadonlyArray<number | null> }>,
  stacked = false,
): EChartsOption {
  return {
    tooltip: { trigger: 'axis' },
    legend: series.length > 1 ? { type: 'scroll', top: 0 } : undefined,
    grid: { left: 56, right: 16, top: series.length > 1 ? 36 : 16, bottom: 40 },
    xAxis: { type: 'category', data: [...categories], axisLabel: { interval: 0, hideOverlap: true } },
    yAxis: { type: 'value', axisLabel },
    series: series.map((item) => ({
      name: item.name,
      type: 'bar',
      stack: stacked ? 'total' : undefined,
      data: [...item.data],
    })),
  };
}

const TIER_COLOURS: Record<string, string> = {
  Bronze: '#c16941',
  Silver: '#bab0ac',
  Gold: '#f1ce63',
  Platinum: '#76b7b2',
  Infinite: '#59a14f',
};

export function tierBarOption(
  name: string,
  tiers: ReadonlyArray<string>,
  data: ReadonlyArray<number | null>,
): EChartsOption {
  return {
    tooltip: { trigger: 'axis' },
    grid: { left: 56, right: 16, top: 28, bottom: 40 },
    xAxis: { type: 'category', data: [...tiers], axisLabel: { interval: 0, hideOverlap: true } },
    yAxis: { type: 'value', axisLabel },
    series: [
      {
        name,
        type: 'bar',
        label: { show: true, position: 'top', formatter: ({ value }) => whole.format(Number(value)) },
        data: tiers.map((tier, index) => ({ value: data[index], itemStyle: { color: TIER_COLOURS[tier] } })),
      },
    ],
  };
}
